import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PaletteIcon from '@mui/icons-material/Palette';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding, useUpdateBranding } from '../../hooks/useBranding';

const FONT_OPTIONS = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Poppins',
  'Playfair Display',
  'Merriweather',
  'Source Sans 3',
  'Raleway',
];

export default function BrandingPage() {
  const { host } = useAuth();
  const { data: branding, isLoading, error } = useBranding();
  const updateMutation = useUpdateBranding();

  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#5C6BC0');
  const [accentColor, setAccentColor] = useState('#FF7043');
  const [fontFamily, setFontFamily] = useState('Inter');
  const [customDomain, setCustomDomain] = useState('');
  const [whiteLabel, setWhiteLabel] = useState(false);
  const [saved, setSaved] = useState(false);

  const isEnterprise = host?.plan === 'ENTERPRISE';

  useEffect(() => {
    if (branding) {
      setLogoUrl(branding.logoUrl ?? '');
      setPrimaryColor(branding.primaryColor ?? '#5C6BC0');
      setAccentColor(branding.accentColor ?? '#FF7043');
      setFontFamily(branding.fontFamily ?? 'Inter');
      setCustomDomain(branding.customDomain ?? '');
      setWhiteLabel(branding.whiteLabel ?? false);
    }
  }, [branding]);

  const handleSave = async () => {
    setSaved(false);
    await updateMutation.mutateAsync({
      logoUrl: logoUrl || null,
      primaryColor: primaryColor || null,
      accentColor: accentColor || null,
      fontFamily: fontFamily || null,
      customDomain: customDomain || null,
      whiteLabel,
    });
    setSaved(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load branding settings. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <PaletteIcon color="primary" />
        <Typography variant="h5">Branding Settings</Typography>
      </Stack>

      <Stack spacing={3} sx={{ maxWidth: 600 }}>
        {/* Logo */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Logo
            </Typography>
            <TextField
              fullWidth
              label="Logo URL"
              placeholder="https://example.com/logo.png"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              size="small"
            />
            {logoUrl && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  bgcolor: 'grey.50',
                }}
              >
                <Box
                  component="img"
                  src={logoUrl}
                  alt="Logo preview"
                  sx={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Colors */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Colors
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: primaryColor,
                    border: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                />
                <TextField
                  fullWidth
                  label="Primary Color"
                  placeholder="#5C6BC0"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  size="small"
                />
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: accentColor,
                    border: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                />
                <TextField
                  fullWidth
                  label="Accent Color"
                  placeholder="#FF7043"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  size="small"
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Font */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Font Family
            </Typography>
            <Select
              fullWidth
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
              size="small"
            >
              {FONT_OPTIONS.map((font) => (
                <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                  {font}
                </MenuItem>
              ))}
            </Select>
          </CardContent>
        </Card>

        {/* Custom Domain */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Custom Domain
            </Typography>
            <TextField
              fullWidth
              label="Custom Domain"
              placeholder="events.yourdomain.com"
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
              size="small"
              helperText="Point a CNAME record to app.timetorsvp.com"
            />
          </CardContent>
        </Card>

        {/* White-label (Enterprise only) */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              White Label
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={whiteLabel}
                  onChange={(e) => setWhiteLabel(e.target.checked)}
                  disabled={!isEnterprise}
                />
              }
              label="Hide TimeToRSVP branding on guest-facing pages"
            />
            {!isEnterprise && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                This feature requires an Enterprise plan.
              </Typography>
            )}
          </CardContent>
        </Card>

        <Divider />

        {/* Live Preview */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Live Preview
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {/* Mock header */}
              <Box sx={{ bgcolor: primaryColor, px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                {logoUrl && (
                  <Box
                    component="img"
                    src={logoUrl}
                    alt="Preview logo"
                    sx={{ height: 28, objectFit: 'contain' }}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <Typography sx={{ color: '#fff', fontFamily, fontWeight: 600 }}>
                  Your Event
                </Typography>
              </Box>
              {/* Mock body */}
              <Box sx={{ p: 3, bgcolor: '#fafafa' }}>
                <Typography sx={{ fontFamily, mb: 1, fontWeight: 600 }}>
                  Saturday Evening Gala
                </Typography>
                <Typography variant="body2" sx={{ fontFamily, color: 'text.secondary', mb: 2 }}>
                  Join us for an unforgettable evening of fine dining and entertainment.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ bgcolor: accentColor, '&:hover': { bgcolor: accentColor }, fontFamily, textTransform: 'none' }}
                >
                  RSVP Now
                </Button>
                {!whiteLabel && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.disabled' }}>
                    Powered by TimeToRSVP
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Save button */}
        {saved && (
          <Alert severity="success">
            Branding settings saved successfully.
          </Alert>
        )}

        {updateMutation.isError && (
          <Alert severity="error">
            Failed to save branding settings. Please try again.
          </Alert>
        )}

        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={updateMutation.isPending}
          sx={{ minHeight: 44, alignSelf: 'flex-start' }}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </Stack>
    </Box>
  );
}
